import React, {FC, useEffect} from 'react'
import {createPortal} from 'react-dom'
import {useNavigate} from 'react-router-dom'
import {getMessageTypeMeta} from '@containers/chat/types'
import {PATH_VOYAGE_STADIUM} from '@constants/navigate'

export interface ChatPopup {
    driverId: number
    driverName?: string
    plate?: string
    routeName?: string
    routeId?: number
    unreadCount: number
    messageType?: string
}

export type ChatToastTarget = {driverId: number; driverName?: string; plate?: string; routeName?: string}

interface Props {
    popups: ChatPopup[]
    onOpen: (target: ChatToastTarget) => void
    onDismiss: (driverId: number) => void
}

// A toast clears itself after this long: the message is not lost (the unread counter and the card
// marker keep it), and a stack that never goes away would cover the board it points at.
const AUTO_DISMISS_MS = 10000

const pluralMessages = (n: number): string => {
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) {
        return 'повідомлення'
    }
    return 'повідомлень'
}

interface ToastProps {
    popup: ChatPopup
    onOpen: (target: ChatToastTarget) => void
    onDismiss: (driverId: number) => void
}

/** One toast, owning its own countdown — hence a component rather than an effect in the list. */
const ChatToast: FC<ToastProps> = ({popup, onOpen, onDismiss}) => {
    const navigate = useNavigate()
    const meta = getMessageTypeMeta(popup.messageType)

    // unreadCount is in the deps on purpose: another message from the SAME driver replaces the
    // toast in place (it is keyed by driverId), and the countdown has to start over.
    useEffect(() => {
        const timer = window.setTimeout(() => onDismiss(popup.driverId), AUTO_DISMISS_MS)
        return () => window.clearTimeout(timer)
    }, [popup.driverId, popup.unreadCount, onDismiss])

    return (
        <div
            className={`vs-chat-toast vs-chat-toast--${meta.severity}`}
            onClick={() => onOpen({
                driverId: popup.driverId,
                driverName: popup.driverName,
                plate: popup.plate,
                routeName: popup.routeName,
            })}
        >
            <div className="vs-chat-toast__head">
                <span className="vs-chat-toast__route">
                    {meta.icon || '💬'} {popup.routeName || 'Новий маршрут'}
                </span>
                <button
                    type="button"
                    className="vs-chat-toast__close"
                    aria-label="Закрити"
                    onClick={(e) => {
                        e.stopPropagation()
                        onDismiss(popup.driverId)
                    }}
                >
                    ×
                </button>
            </div>
            <div className="vs-chat-toast__body">
                {popup.driverName || `Водій #${popup.driverId}`}
                {popup.plate ? ` (${popup.plate})` : ''}: {popup.unreadCount} {pluralMessages(popup.unreadCount)}
            </div>

            {/* Маршрут може бути не відкритий на стадіоні — звідси спосіб потрапити саме до нього.
              * Через URL, а не через стан стадіону: тост не має знати про його внутрішню кухню,
              * і посилання лишається робочим після перезавантаження. */}
            {popup.routeId != null && (
                <button
                    type="button"
                    className="vs-chat-toast__action"
                    onClick={(e) => {
                        e.stopPropagation()
                        navigate(`${PATH_VOYAGE_STADIUM}?focusRoute=${popup.routeId}`)
                        onDismiss(popup.driverId)
                    }}
                >
                    Показати на схемі
                </button>
            )}
        </div>
    )
}

/**
 * Toast notifications for new messages (ported from the legacy GlobalChat.showPopup):
 * route, driver and unread count; a click opens the thread, the cross dismisses it, and each
 * toast also clears itself after AUTO_DISMISS_MS.
 */
const ChatNotifications: FC<Props> = ({popups, onOpen, onDismiss}) => {
    if (popups.length === 0) {
        return null
    }

    return createPortal(
        <div className="vs-chat-toasts">
            {popups.map((p) => (
                <ChatToast key={p.driverId} popup={p} onOpen={onOpen} onDismiss={onDismiss}/>
            ))}
        </div>,
        document.body,
    )
}

export default ChatNotifications
